import React, { useEffect, useState } from "react";
import { Header, Loader, Message } from "semantic-ui-react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { fetchSummary } from "../../services/statsApi";
import "./stats.css";

const GENDER_COLORS = {
  female: "#e84a8a",
  male: "#3989d6",
  other: "#888888",
  declined: "#555555",
};

const GENDER_LABELS = {
  female: "Femmes",
  male: "Hommes",
  other: "Autre",
  declined: "Préfère ne pas dire",
};

const LANG_COLORS = ["#3989d6", "#e84a8a", "#8bc34a", "#ff9800", "#9c27b0", "#00bcd4"];

// Mongo $dayOfWeek : 1=dim..7=sam
const DOW_LABELS = { 1: "Dim", 2: "Lun", 3: "Mar", 4: "Mer", 5: "Jeu", 6: "Ven", 7: "Sam" };

// Styles partagés pour les tooltips Recharts (texte clair sur fond sombre).
const TOOLTIP_CONTENT_STYLE = {
  background: "#1f1f1d",
  border: "1px solid #555",
  borderRadius: 4,
  color: "white",
};
const TOOLTIP_LABEL_STYLE = { color: "white", fontWeight: "bold" };
const TOOLTIP_ITEM_STYLE = { color: "white" };

function pivotTimeseries(rows) {
  // [{date, lang, count}, ...] -> [{date, fr: 12, en: 5, ...}, ...]
  const byDate = new Map();
  rows.forEach((r) => {
    if (!byDate.has(r.date)) byDate.set(r.date, { date: r.date });
    byDate.get(r.date)[r.lang] = r.count;
  });
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function topLangs(rows, n = 5) {
  const totals = new Map();
  rows.forEach((r) => totals.set(r.lang, (totals.get(r.lang) || 0) + r.count));
  return Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([lang]) => lang);
}

const Stats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchSummary()
      .then((d) => mounted && setData(d))
      .catch((e) => mounted && setError(e.response?.data?.message || "Erreur de chargement"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="admin-stats-page">
        <Loader active inline="centered" inverted>Chargement…</Loader>
      </div>
    );
  }
  if (error) {
    return (
      <div className="admin-stats-page">
        <Message negative>{error}</Message>
      </div>
    );
  }
  if (!data) return null;

  const v = data.visits || {};
  const ts = v.timeseries || [];
  const langs = topLangs(ts, 5);
  const tsPivoted = pivotTimeseries(ts);
  const dow = (v.byDayOfWeek || []).map((r) => ({
    label: DOW_LABELS[r.dow] || r.dow,
    count: r.count,
  }));

  const genderPie = ["female", "male", "other", "declined"]
    .map((g) => ({
      key: g,
      name: GENDER_LABELS[g],
      value: v.genders?.[g] || 0,
    }))
    .filter((d) => d.value > 0);

  const variation = v.monthOverMonthPct || 0;
  const variationColor =
    variation > 0 ? "#4caf50" : variation < 0 ? "#f44336" : "#aaa";

  return (
    <div className="admin-stats-page">
      <Header as="h2" inverted>Statistiques</Header>

      {/* Big-figures */}
      <section className="stats-section">
        <Header as="h3" inverted>Visiteurs uniques</Header>
        <div className="stats-bigfigures">
          <div className="stats-bigfigure">
            <div className="stats-bigfigure-value">{v.totalUnique || 0}</div>
            <div className="stats-bigfigure-label">total</div>
          </div>
          <div className="stats-bigfigure">
            <div className="stats-bigfigure-value">{v.thisMonth || 0}</div>
            <div className="stats-bigfigure-label">ce mois</div>
          </div>
          <div className="stats-bigfigure">
            <div className="stats-bigfigure-value" style={{ color: variationColor }}>
              {variation > 0 ? "+" : ""}{variation}%
            </div>
            <div className="stats-bigfigure-label">vs mois précédent</div>
          </div>
        </div>
      </section>

      {/* Time series */}
      <section className="stats-section">
        <Header as="h3" inverted>Visites par jour (90 derniers jours)</Header>
        {tsPivoted.length === 0 ? (
          <div className="stats-empty">Pas encore de données.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={tsPivoted}>
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis dataKey="date" stroke="#aaa" tick={{ fontSize: 10 }} />
              <YAxis stroke="#aaa" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
              <Legend />
              {langs.map((lang, i) => (
                <Line
                  key={lang}
                  type="monotone"
                  dataKey={lang}
                  stroke={LANG_COLORS[i % LANG_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </section>

      {/* Day of week */}
      <section className="stats-section">
        <Header as="h3" inverted>Fréquentation par jour de la semaine</Header>
        {dow.length === 0 ? (
          <div className="stats-empty">Pas encore de données.</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={dow}>
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis dataKey="label" stroke="#aaa" />
              <YAxis stroke="#aaa" />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
              <Bar dataKey="count" fill="#3989d6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>

      {/* Donut genre */}
      <section className="stats-section">
        <Header as="h3" inverted>Genre des visiteurs</Header>
        {genderPie.length === 0 ? (
          <div className="stats-empty">Aucune réponse encore.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={genderPie}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
              >
                {genderPie.map((d) => (
                  <Cell key={d.key} fill={GENDER_COLORS[d.key]} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </section>

      {/* Stacked bar genre par langue */}
      <section className="stats-section">
        <Header as="h3" inverted>Genre par langue (top 5)</Header>
        {(v.gendersByLang || []).length === 0 ? (
          <div className="stats-empty">Aucune réponse encore.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={v.gendersByLang}>
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis dataKey="lang" stroke="#aaa" />
              <YAxis stroke="#aaa" />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
              <Legend />
              <Bar dataKey="female" stackId="g" fill={GENDER_COLORS.female} name="Femmes" />
              <Bar dataKey="male" stackId="g" fill={GENDER_COLORS.male} name="Hommes" />
              <Bar dataKey="other" stackId="g" fill={GENDER_COLORS.other} name="Autre" />
              <Bar dataKey="declined" stackId="g" fill={GENDER_COLORS.declined} name="Préfère ne pas dire" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>

    </div>
  );
};

export default Stats;
